"use client"

import { useState, useEffect, useCallback } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card"
import { Button } from "../components/ui/button"
import { toast } from "react-hot-toast"
import { Calendar, X, ArrowDownCircle } from "lucide-react"
import moment from "../utils/timezone"
import DateRangePicker from '../components/ui/date-range-picker'
import { defaultDateRange } from "../utils/timezone"
import { tokenReserveService } from "../apis/tokenReserveService"
import TokenReserveWithdrawModal from "../components/token-reserve-withdraw-modal"

export default function TokenReserves() {
  const [loading, setLoading] = useState(true);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetAllLoading, setResetAllLoading] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [resetType, setResetType] = useState(null); // 'daily' or 'all'
  const [tokenData, setTokenData] = useState({
    dealerTips: 0,
    waitressTips: 0,
    totalTokens: 0
  });
  const [balanceSummary, setBalanceSummary] = useState({
    totalTaxAllTime: 0,
    totalWithdrawn: 0,
    currentBalance: 0,
  });
  const [withdrawalLogs, setWithdrawalLogs] = useState([]);

  const [dateRange, setDateRange] = useState(defaultDateRange);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [tokenHistory, setTokenHistory] = useState([]);

  const fetchWithdrawalLogs = useCallback(async () => {
    const response = await tokenReserveService.getWithdrawals();
    if (response.success) {
      const logs = Array.isArray(response.data?.data)
        ? response.data.data
        : Array.isArray(response.data)
          ? response.data
          : [];
      setWithdrawalLogs(logs);
    } else {
      setWithdrawalLogs([]);
    }
  }, []);

  const fetchTokenData = useCallback(async () => {
    try {
      setLoading(true);
      const [response, balanceResponse] = await Promise.all([
        tokenReserveService.getTokenReserve(
          moment(dateRange.start).tz('America/Chicago').format('YYYY-MM-DD'),
          moment(dateRange.end).tz('America/Chicago').format('YYYY-MM-DD')
        ),
        tokenReserveService.getBalanceSummary(),
        fetchWithdrawalLogs(),
      ]);
  
 
      if (response.success) {
        // Fix: Access the nested data structure correctly
        const data = response.data.data; // Get the inner data object
        
  
        // Map the API response to match component expectations
        const tokenData = {
          dealerTips: parseFloat(data.dealerTax || 0),
          waitressTips: parseFloat(data.waitressTax || 0),
          totalTokens: parseFloat(data.totalTax || 0)
        };
        

        setTokenData(tokenData);

        const summary = response.data.data?.balanceSummary
          || balanceResponse.data?.data
          || balanceResponse.data;
        if (summary) {
          setBalanceSummary({
            totalTaxAllTime: parseFloat(summary.totalTaxAllTime || 0),
            totalWithdrawn: parseFloat(summary.totalWithdrawn || 0),
            currentBalance: parseFloat(summary.currentBalance || 0),
          });
        } else if (balanceResponse.success) {
          const balanceData = balanceResponse.data?.data || balanceResponse.data;
          setBalanceSummary({
            totalTaxAllTime: parseFloat(balanceData?.totalTaxAllTime || 0),
            totalWithdrawn: parseFloat(balanceData?.totalWithdrawn || 0),
            currentBalance: parseFloat(balanceData?.currentBalance || 0),
          });
        }
        
        // Process detailed records into history format
        if (data.detailedRecords && Array.isArray(data.detailedRecords)) {
      
          // Group records by date and calculate daily totals
          const groupedByDate = data.detailedRecords.reduce((acc, record) => {
            const date = moment(record.date).tz('America/Chicago').format('YYYY-MM-DD');
            
            if (!acc[date]) {
              acc[date] = {
                date: record.date,
                dealerTips: 0,
                waitressTips: 0,
                totalTokens: 0
              };
            }
            
            const taxAmount = parseFloat(record.tax || 0);
            
            if (record.source === 'Dealer') {
              acc[date].dealerTips += taxAmount;
            } else if (record.source === 'Waitress') {
              acc[date].waitressTips += taxAmount;
            }
            
            acc[date].totalTokens += taxAmount;
            
            return acc;
          }, {});
          
 
          // Convert grouped data to array and sort by date (newest first)
          const formattedHistory = Object.values(groupedByDate)
            .sort((a, b) => new Date(b.date) - new Date(a.date))
            .map((entry, index) => ({
              id: index + 1,
              date: entry.date,
              dealerTips: entry.dealerTips,
              waitressTips: entry.waitressTips,
              totalTokens: entry.totalTokens
            }));
          
 
          setTokenHistory(formattedHistory);
        } else {
          setTokenHistory([]);
        }
      } else {
        throw new Error(response.error || "Failed to fetch token data");
      }
    } catch (error) {
      console.error("Error fetching token reserves:", error);
      toast.error("Failed to load token reserves data");
      // Reset data on error
      setTokenData({
        dealerTips: 0,
        waitressTips: 0,
        totalTokens: 0
      });
      setTokenHistory([]);
      setBalanceSummary({
        totalTaxAllTime: 0,
        totalWithdrawn: 0,
        currentBalance: 0,
      });
    } finally {
      setLoading(false);
    }
  }, [dateRange, fetchWithdrawalLogs]);

  const handleWithdraw = async ({ amount, message }) => {
    const result = await tokenReserveService.withdraw({ amount, message });
    if (result.success) {
      await fetchTokenData();
    }
    return result;
  };

  useEffect(() => {
    fetchTokenData();
  }, [fetchTokenData]);

  // const openResetModal = (type) => {
  //   setResetType(type);
  //   setShowResetModal(true);
  // };

  const closeResetModal = () => {
    setShowResetModal(false);
    setResetType(null);
  };

  const handleResetDailyTokens = async () => {
    try {
      setResetLoading(true);
      const response = await tokenReserveService.resetDailyTokens();

      if (response.success) {
        toast.success(response.message || "Daily tokens reset successfully!");
        // Refresh the data after reset
        await fetchTokenData();
      } else {
        throw new Error(response.error || "Failed to reset daily tokens");
      }
    } catch (error) {
      console.log(error);
      console.error("Error resetting daily tokens:", error);
      toast.error("Failed to reset daily tokens");
    } finally {
      setResetLoading(false);
      closeResetModal();
    }
  };

  const handleResetAllTokens = async () => {
    try {
      setResetAllLoading(true);
      const response = await tokenReserveService.resetAllTokens();
      
      if (response.success) {
        toast.success(response.message || "All tokens reset successfully!");
        // Refresh the data after reset
        await fetchTokenData();
      } else {
        throw new Error(response.error || "Failed to reset all tokens");
      }
    } catch (error) {
      console.error("Error resetting all tokens:", error);
      toast.error("Failed to reset all tokens");
    } finally {
      setResetAllLoading(false);
      closeResetModal();
    }
  };

  const confirmReset = () => {
    if (resetType === 'daily') {
      handleResetDailyTokens();
    } else if (resetType === 'all') {
      handleResetAllTokens();
    }
  };

  const formatCurrency = (value) => {
    return parseFloat(value || 0).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const formatDateRange = () => {
    return `${moment(dateRange.start).tz('America/Chicago').format('MMM D, YYYY')} - ${moment(dateRange.end).tz('America/Chicago').format('MMM D, YYYY')}`;
  };

  const handleDateRangeApply = (start, end) => {
    setDateRange({ start, end });
    setIsDatePickerOpen(false);
  };

  return (
    <div className="p-6 bg-gray-50">
      <div className="w-full mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold">Token Reserves Management</h1>
          
          <div 
            className="flex items-center gap-2 text-sm text-gray-700 hover:text-blue-600 px-3 py-1.5 rounded-md border border-gray-300 hover:border-blue-500 shadow-sm transition-colors duration-200 cursor-pointer"
            onClick={() => setIsDatePickerOpen(true)}
          >
            <Calendar className="w-4 h-4" />
            <span className="font-medium">{formatDateRange()}</span>
          </div>
        </div>
        
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-blue-500"></div>
          </div>
        ) : (
          <>
            {/* Token Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <Card className="bg-green-50 border-green-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg text-green-700">Dealer Tips Tax</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold text-green-600">${formatCurrency(tokenData.dealerTips)}</p>
                </CardContent>
              </Card>
              
              <Card className="bg-blue-50 border-blue-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg text-blue-700">Waitress Tips Tax</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold text-blue-600">${formatCurrency(tokenData.waitressTips)}</p>
                </CardContent>
              </Card>
              
              <Card className="bg-yellow-50 border-yellow-200">
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between gap-3">
                    <CardTitle className="text-lg text-yellow-700">Total Token Reserve</CardTitle>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-yellow-400 text-yellow-800 hover:bg-yellow-100"
                      onClick={() => setShowWithdrawModal(true)}
                    >
                      <ArrowDownCircle className="w-4 h-4 mr-1" />
                      Withdraw
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold text-yellow-600">
                    ${formatCurrency(balanceSummary.currentBalance)}
                  </p>
                  <div className="mt-3 text-sm text-yellow-800 space-y-1">
                    <p>All-time tax collected: ${formatCurrency(balanceSummary.totalTaxAllTime)}</p>
                    <p>Total withdrawn: ${formatCurrency(balanceSummary.totalWithdrawn)}</p>
                  </div>
                </CardContent>
              </Card>
            </div>
            
            {/* Token History */}
            <Card className="mb-8">
              <CardHeader>
                <CardTitle>Daily Token Tax History</CardTitle>
                <p className="text-sm text-gray-600">Tax collected from Dealer and Waitress tips by date</p>
              </CardHeader>
              <CardContent>
                {tokenHistory.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left">
                      <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                        <tr>
                          <th className="px-6 py-3">Date</th>
                          <th className="px-6 py-3">Dealer Tips Tax</th>
                          <th className="px-6 py-3">Waitress Tips Tax</th>
                          <th className="px-6 py-3">Total Tax</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tokenHistory.map((entry) => (
                          <tr key={entry.id} className="bg-white border-b hover:bg-gray-50">
                            <td className="px-6 py-4 font-medium">{moment(entry.date).tz('America/Chicago').format('MMM D, YYYY')}</td>
                            <td className="px-6 py-4 text-green-600">${formatCurrency(entry.dealerTips)}</td>
                            <td className="px-6 py-4 text-blue-600">${formatCurrency(entry.waitressTips)}</td>
                            <td className="px-6 py-4 font-bold text-yellow-600">${formatCurrency(entry.totalTokens)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    No token history available for the selected date range
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Withdrawal Logs</CardTitle>
                <p className="text-sm text-gray-600">
                  History of token reserve withdrawals with who deducted, when, and how much
                </p>
              </CardHeader>
              <CardContent>
                {withdrawalLogs.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left">
                      <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                        <tr>
                          <th className="px-6 py-3">Date & Time</th>
                          <th className="px-6 py-3">Withdrawn By</th>
                          <th className="px-6 py-3">Amount</th>
                          <th className="px-6 py-3">Balance Before</th>
                          <th className="px-6 py-3">Balance After</th>
                          <th className="px-6 py-3">Message</th>
                        </tr>
                      </thead>
                      <tbody>
                        {withdrawalLogs.map((log) => (
                          <tr key={log.id} className="bg-white border-b hover:bg-gray-50 align-top">
                            <td className="px-6 py-4 whitespace-nowrap">
                              {moment(log.createdAt).tz('America/Chicago').format('MMM D, YYYY h:mm A')}
                            </td>
                            <td className="px-6 py-4 font-medium">{log.withdrawnBy}</td>
                            <td className="px-6 py-4 text-red-600 font-semibold">
                              -${formatCurrency(log.amount)}
                            </td>
                            <td className="px-6 py-4">${formatCurrency(log.balanceBefore)}</td>
                            <td className="px-6 py-4 font-semibold text-yellow-700">
                              ${formatCurrency(log.balanceAfter)}
                            </td>
                            <td className="px-6 py-4 text-gray-700 max-w-md whitespace-pre-wrap">
                              {log.message}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    No withdrawals recorded yet
                  </div>
                )}
              </CardContent>
            </Card>

          </>
        )}
        
        {/* Reset Buttons - Moved to bottom */}
        {/* <div className="flex justify-center gap-4 mb-8">
          <button
            onClick={() => openResetModal('daily')}
            disabled={resetLoading || resetAllLoading}
            className={`
              flex items-center gap-2 px-6 py-3 rounded-lg font-medium text-white
              transition-all duration-200 shadow-lg hover:shadow-xl
              ${resetLoading || resetAllLoading
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-red-500 hover:bg-red-600 active:bg-red-700'
              }
            `}
          >
            <RotateCcw className="w-5 h-5" />
            <span>Reset Daily Tokens</span>
          </button>
          
          <button
            onClick={() => openResetModal('all')}
            disabled={resetLoading || resetAllLoading}
            className={`
              flex items-center gap-2 px-6 py-3 rounded-lg font-medium text-white
              transition-all duration-200 shadow-lg hover:shadow-xl
              ${resetLoading || resetAllLoading
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-red-700 hover:bg-red-800 active:bg-red-900'
              }
            `}
          >
            <Trash2 className="w-5 h-5" />
            <span>Reset All Tokens</span>
          </button>
        </div> */}
      </div>
      
      {/* Date Range Picker */}
      {isDatePickerOpen && (
        <DateRangePicker
          isOpen={isDatePickerOpen}
          onClose={() => setIsDatePickerOpen(false)}
          onApply={handleDateRangeApply}
          initialStartDate={dateRange.start}
          initialEndDate={dateRange.end}
        />
      )}

      <TokenReserveWithdrawModal
        isOpen={showWithdrawModal}
        onClose={() => setShowWithdrawModal(false)}
        currentBalance={balanceSummary.currentBalance}
        onWithdraw={handleWithdraw}
      />

      {/* Confirmation Modal */}
      {showResetModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-800">
                Confirm Reset
              </h3>
              <button
                onClick={closeResetModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-600">
                Are you sure you want to reset {resetType === 'daily' ? 'daily' : 'all'} tokens? 
                This action cannot be undone.
              </p>
              {resetType === 'all' && (
                <p className="text-red-600 text-sm mt-2 font-medium">
                  Warning: This will reset ALL token data in the database.
                </p>
              )}
            </div>
            
            <div className="flex justify-end gap-3">
              <button
                onClick={closeResetModal}
                disabled={resetLoading || resetAllLoading}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                No
              </button>
              <button
                onClick={confirmReset}
                disabled={resetLoading || resetAllLoading}
                className={`
                  px-4 py-2 text-white rounded-md transition-all duration-200 flex items-center gap-2
                  ${resetLoading || resetAllLoading
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : resetType === 'daily' 
                      ? 'bg-red-500 hover:bg-red-600' 
                      : 'bg-red-700 hover:bg-red-800'
                  }
                `}
              >
                {(resetLoading || resetAllLoading) && (
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                )}
                <span>
                  {resetLoading || resetAllLoading ? 'Resetting...' : 'Yes'}
                </span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}